import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Flower2 } from "lucide-react";
import DahliasShell from "../components/dahlias/DahliasShell";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { HERITAGE_GARDEN_PUBLIC_DISPLAY } from "../config/features";
import { useSiteText } from "../store/useSiteContent";
import CmsText from "../components/CmsText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HeritageGarden() {
  useDocumentTitle(
    "Heritage Garden, Breeders' Bed",
    "A summer garden bed at Heritage Dahlias dedicated to new dahlia seedlings bred by growers across Aotearoa."
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const heading = useSiteText("garden.heading", "The Heritage Garden *Breeders\u2019 Bed.*");
  const intro = useSiteText(
    "garden.intro",
    "A small bed at our family farm given over to breeders growing something new. Each bloom is hand-tended through the summer and photographed at its peak, alongside the breeder who raised it."
  );

  useEffect(() => {
    axios
      .get(`${API}/seedlings/garden`)
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  // Goes live automatically once the first seedling is published
  // (flag stays as a manual override to force it on regardless).
  if (!HERITAGE_GARDEN_PUBLIC_DISPLAY && !loading && rows.length === 0) {
    return <Navigate to="/breeders-garden" replace />;
  }

  return (
    <DahliasShell>
      <section className="relative border-b-2 border-[var(--berry)]/20" style={{ backgroundColor: "#FAF5EB" }}>
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-12 md:pb-16 text-center">
          <span className="inline-flex items-center gap-3 px-4 py-1.5 border border-[var(--berry)]/50 rounded-full font-display tracking-[0.4em] text-xs text-[var(--berry)] uppercase" style={{ backgroundColor: "rgba(250, 245, 235, 0.85)" }}>
            <span aria-hidden="true">✦</span>
            Summer 2026, 2027
            <span aria-hidden="true">✦</span>
          </span>
          <CmsText
            as="h1"
            text={heading}
            italicClass="italic text-[var(--berry)]"
            className="font-display text-4xl md:text-6xl text-[var(--dahlias-ink)] mt-6 leading-[1.05]"
          />
          <p className="mt-7 text-base md:text-lg text-[var(--dahlias-ink)]/85 max-w-2xl mx-auto leading-relaxed whitespace-pre-line">
            {intro}
          </p>
          <Link
            to="/breeders-garden"
            data-testid="heritage-garden-submit-cta"
            className="mt-9 inline-flex items-center gap-3 bg-[var(--dahlias-ink)] hover:bg-[var(--berry-deep)] text-[#FBE6D8] px-7 py-3.5 font-display tracking-[0.18em] text-xs uppercase rounded-full transition-all"
          >
            Submit your seedling <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
        {loading ? (
          <p className="text-center text-[var(--dahlias-ink)]/70 font-display italic">Loading the garden…</p>
        ) : rows.length === 0 ? (
          <div className="text-center max-w-xl mx-auto">
            <Flower2 className="w-12 h-12 mx-auto text-[var(--berry)]" />
            <p className="mt-5 text-lg font-display italic text-[var(--dahlias-ink)]/85">
              The first blooms are still tucked beneath the soil. Check back as the summer
              warms, the bed will fill in over the coming weeks.
            </p>
          </div>
        ) : (
          <ul className="grid gap-7 md:gap-9 md:grid-cols-2 lg:grid-cols-3">
            {rows.map((b) => (
              <li key={b.id} data-testid={`heritage-garden-card-${b.id}`} className="border-2 border-[var(--berry)]/20 rounded-2xl overflow-hidden bg-[#FAF5EB] shadow-[0_8px_24px_rgba(110,30,58,0.08)]">
                {b.photos?.[0] && (
                  <div className="aspect-[4/5] overflow-hidden bg-[#FBE6D8]">
                    <img src={b.photos[0]} alt={b.dahlia_name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="p-5 md:p-6">
                  <p className="font-display tracking-[0.3em] text-[10px] uppercase text-[var(--berry)]">
                    Bred by {b.breeder_name}{b.breeder_region ? ` · ${b.breeder_region}` : ""}
                  </p>
                  <h3 className="mt-2 font-display text-2xl text-[var(--dahlias-ink)]">{b.dahlia_name}</h3>
                  {b.parentage && (
                    <p className="mt-1 text-sm italic text-[var(--berry)]">{b.parentage}</p>
                  )}
                  {b.description && (
                    <p className="mt-3 text-sm text-[var(--dahlias-ink)]/85 leading-relaxed">
                      {b.description}
                    </p>
                  )}
                  {b.breeder_bio && (
                    <p className="mt-4 pt-4 border-t border-[var(--berry)]/15 text-xs italic text-[var(--dahlias-ink)]/70 leading-relaxed">
                      {b.breeder_bio}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </DahliasShell>
  );
}
